import { useState, useRef, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import { Menu, X, Plus, FolderOpen, Settings, Loader2, Play, Handshake, ArrowRight } from "lucide-react"
import { cn, getInitials } from "@/lib/utils"
// Import navigation data from the main sidebar to ensure consistency
import { mainNav } from "./sidebar"
import { useAppSelector } from "@/app/store/hooks"
import { useGetAllFoldersQuery } from "@/app/store/api/pitchDeckApi"
import { CreateFolderModal } from "@/components/modal-dialogs/create-folder-modal/create-folder-modal"
import { PartnerWithUsModal } from "@/components/modal-dialogs/partner-with-us-modal/partner-with-us-modal"
import { SettingsModal } from "@/components/modal-dialogs/settings-modal/settings-modal"
import { SocialsDropup, WhatsAppIcon } from "@/components/dashboard/socials-dropup"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"

export function MobileNav() {
    const [open, setOpen] = useState(false)
    const pathname = usePathname()
    const { user } = useAppSelector((state) => state.auth)

    const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false)
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false)
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

    // Scroll state for gradient (Bottom only)
    const [showBottomGradient, setShowBottomGradient] = useState(true)
    const folderContainerRef = useRef<HTMLDivElement>(null)

    const handleScroll = () => {
        if (folderContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = folderContainerRef.current
            // Show bottom gradient if not at the verrry bottom
            setShowBottomGradient(scrollHeight - scrollTop - clientHeight > 5)
        }
    }

    // Effect to check bottom gradient on mount/resize
    useEffect(() => {
        if (open) {
            const checkScroll = () => {
                handleScroll();
            }
            // Initial check
            checkScroll();
            window.addEventListener('resize', checkScroll)
            return () => window.removeEventListener('resize', checkScroll)
        }
    }, [open])

    // Fetch folders from API
    const { data: foldersData, isLoading: foldersLoading } = useGetAllFoldersQuery(undefined, {
        skip: !open, // Only fetch when menu is open
    })
    // Filter out placeholder folders
    const PLACEHOLDER_FOLDERS = ["personal", "work", "archive"]
    const folders = (foldersData?.data || []).filter(
        folder => !PLACEHOLDER_FOLDERS.includes(folder.name.toLowerCase())
    )

    const initials = getInitials(user?.firstName, user?.lastName);

    return (
        <div className="md:hidden">
            <button
                onClick={() => setOpen(true)}
                className="p-2 -ml-2 text-slate-600 hover:text-slate-900"
            >
                <Menu className="h-6 w-6" />
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
                        />

                        {/* Sidebar Drawer */}
                        <motion.div
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 200 }}
                            className="fixed inset-y-0 left-0 z-50 w-72 bg-[#F8FAFC] shadow-xl flex flex-col h-full border-r border-gray-100"
                        >
                            {/* Header */}
                            <div className="flex h-16 items-center justify-between px-6 border-b border-gray-100 flex-shrink-0">
                                <div className="flex flex-col">
                                    <Link href="/" className="font-bold text-xl leading-none" onClick={() => setOpen(false)}>
                                        Deckwale
                                    </Link>
                                    <span className="text-[10px] font-semibold text-blue-600 leading-none mt-2">by Seedicon</span>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="p-2 -mr-2 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 flex flex-col min-h-0 overflow-hidden py-6">
                                <div className="px-4 flex-shrink-0">
                                    <nav className="grid gap-1">
                                        {mainNav.map((item, index) => {
                                            const isActive = pathname === item.href
                                            const Icon = item.icon
                                            return (
                                                <Link
                                                    key={index}
                                                    href={item.href}
                                                    onClick={() => setOpen(false)}
                                                    className={cn(
                                                        "relative group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                                        isActive
                                                            ? "bg-slate-100 text-slate-900"
                                                            : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                                                    )}
                                                >
                                                    {isActive && (
                                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-1 rounded-r-full bg-gradient-to-b from-blue-400 to-indigo-600" />
                                                    )}
                                                    <Icon className={cn("h-4 w-4 transition-colors", isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-900")} />
                                                    {item.title}
                                                </Link>
                                            )
                                        })}
                                    </nav>
                                </div>

                                <div className="mx-7 my-6 h-px bg-[#e4edff] flex-shrink-0" />

                                <div className="flex-1 flex flex-col min-h-0 relative">
                                    <div className="flex items-center justify-between px-6 text-xs font-semibold uppercase text-[#A3B3D1] mb-2 flex-shrink-0">
                                        <span>Folders</span>
                                        <Tooltip title="Add Folder" placement="right" arrow>
                                            <IconButton
                                                size="small"
                                                className="!text-[#A3B3D1] hover:!text-black transition-colors"
                                                onClick={() => setIsCreateFolderModalOpen(true)}
                                            >
                                                <Plus className="h-4 w-4 cursor-pointer" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>

                                    {/* Bottom Scroll Gradient Overlay */}
                                    <div
                                        className={cn(
                                            "absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#F8FAFC] via-[#F8FAFC]/80 to-transparent z-10 pointer-events-none transition-opacity duration-300",
                                            showBottomGradient ? "opacity-100" : "opacity-0"
                                        )}
                                    />

                                    {/* Scrollable Folder List */}
                                    <div
                                        className="flex-1 overflow-y-auto px-4 pb-2"
                                        ref={folderContainerRef}
                                        onScroll={handleScroll}
                                    >
                                        <nav className="grid gap-1">
                                            {foldersLoading ? (
                                                <div className="flex items-center justify-center py-4">
                                                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                                </div>
                                            ) : (
                                                folders.map((folder) => {
                                                    const isActive = pathname === `/folders/${folder._id}`

                                                    return (
                                                        <Link
                                                            key={folder._id}
                                                            href={`/folders/${folder._id}`}
                                                            onClick={() => setOpen(false)}
                                                            className={cn(
                                                                "relative group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                                                isActive
                                                                    ? "bg-slate-100 text-slate-900"
                                                                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                                                            )}
                                                        >
                                                            {isActive && (
                                                                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-1 rounded-r-full bg-gradient-to-b from-blue-400 to-indigo-600" />
                                                            )}
                                                            {folder.logo ? (
                                                                <img src={folder.logo} alt="" className="h-4 w-4 rounded object-cover" />
                                                            ) : (
                                                                <FolderOpen className={cn("h-4 w-4 transition-colors", isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-900")} />
                                                            )}
                                                            {folder.name}
                                                        </Link>
                                                    )
                                                })
                                            )}
                                        </nav>
                                    </div>
                                </div>
                            </div>

                            {/* Options Section */}
                            <div className="px-3 border-t border-gray-100 flex-shrink-0 flex flex-col gap-3 py-4">
                                {/* Watch Demo Button */}
                                <a
                                    href="https://www.youtube.com/@DeckwaleAI"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-start gap-4 px-4 py-0 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors cursor-pointer"
                                >
                                    <Play className="h-4 w-4 text-slate-400 group-hover:text-red-600 transition-colors" />
                                    <span>Watch Demo</span>
                                </a>

                                {/* WhatsApp Community Button */}
                                <a
                                    href="https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-start gap-4 px-4 py-0 text-xs font-medium text-slate-500 hover:text-emerald-600 transition-colors cursor-pointer"
                                >
                                    <WhatsAppIcon className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                                    <span>Join Community</span>
                                </a>

                                <SocialsDropup isCollapsed={false} />

                                {/* Partner Button */}
                                <button
                                    onClick={() => setIsPartnerModalOpen(true)}
                                    className="relative cursor-pointer w-full group overflow-hidden rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-left shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all duration-300 hover:shadow-[0_8px_16px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 hover:border-blue-200 ring-offset-2 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-50/30 to-blue-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                    <div className="relative flex items-center justify-between">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-xs font-bold text-slate-800 tracking-tight">Partner with us</span>
                                            <div className="flex items-center gap-1 group/text">
                                                <span className="text-[10px] font-medium text-slate-500 group-hover:text-blue-600 transition-colors">Grow together</span>
                                                <ArrowRight className="w-2.5 h-2.5 text-slate-400 -ml-0.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-blue-600 transition-all duration-300" />
                                            </div>
                                        </div>
                                        <div className="h-8 w-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-blue-600 group-hover:border-blue-600 transition-all duration-300 shadow-sm group-hover:shadow-blue-200">
                                            <Handshake className="w-3.5 h-3.5 text-slate-400 group-hover:text-white transition-colors" />
                                        </div>
                                    </div>
                                </button>
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-gray-100 bg-[#F8FAFC] flex-shrink-0">
                                <div className="flex items-center gap-3 px-2">
                                    <div className="h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-md">
                                        <span className="text-xs font-bold text-white">
                                            {initials}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-700">
                                            {user?.firstName || "User"}
                                        </p>
                                    </div>
                                    <Settings
                                        className="h-4 w-4 text-[#A3B3D1] cursor-pointer hover:text-slate-600"
                                        onClick={() => setIsSettingsModalOpen(true)}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <CreateFolderModal
                isOpen={isCreateFolderModalOpen}
                onClose={() => setIsCreateFolderModalOpen(false)}
            />
            <PartnerWithUsModal
                isOpen={isPartnerModalOpen}
                onClose={() => setIsPartnerModalOpen(false)}
            />
            <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
            />
        </div>
    )
}
